import { MessageDto, MessageStatus } from 'common/dto-types/message-dto';
import { useNavigate } from 'react-router';
import {
    IconButton,
    Stack,
    Tooltip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
} from '@mui/material';
import {
    UndoOutlined,
    EyeOutlined,
    EditOutlined,
    DeleteOutlined,
    CopyOutlined,
    HistoryOutlined,
} from '@ant-design/icons';
import { useState } from 'react';
import { recallMessage, deleteMessage, duplicateMessage } from '../api';
import { showSuccessToast, showErrorToast } from 'components/message-form/utils';

// Icon colors
const PRIMARY_COLOR = '#1976d2'; // Blue for Edit/View
const SUCCESS_COLOR = '#2E7D32'; // Green for Duplicate
const ERROR_COLOR = '#d32f2f'; // Red for Delete/Recall
const DEFAULT_COLOR = 'default'; // default (grey) for Logs

const MessageActionsCell = ({ row }: { row: { original: MessageDto } }) => {
    const navigate = useNavigate();
    const message = row.original;
    const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
    const [showDuplicateConfirm, setShowDuplicateConfirm] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isDuplicating, setIsDuplicating] = useState(false);
    const [showRecallConfirm, setShowRecallConfirm] = useState(false);
    const [isRecalling, setIsRecalling] = useState(false);

    const handleViewLogs = () => {
        navigate(`/message/history/id/${message.id}`);
    };

    const handleEdit = () => {
        navigate(`/message/detail/${message.id}`);
    };

    const handleView = () => {
        navigate(`/message/detail/${message.id}`);
    };

    const handleDuplicateClick = () => {
        setShowDuplicateConfirm(true);
    };

    const handleDuplicateConfirm = async () => {
        setShowDuplicateConfirm(false);
        setIsDuplicating(true);
        try {
            await duplicateMessage(message.id);
            showSuccessToast('Message duplicated successfully!');
            navigate(`/message/draft`);
        } catch (error: any) {
            showErrorToast(error?.response?.data?.message || 'Failed to duplicate message');
        } finally {
            setIsDuplicating(false);
        }
    };

    const handleDuplicateCancel = () => {
        setShowDuplicateConfirm(false);
    };

    const handleDeleteClick = () => {
        setShowDeleteConfirm(true);
    };

    const handleDeleteConfirm = async () => {
        setShowDeleteConfirm(false);
        setIsDeleting(true);
        try {
            await deleteMessage(message.id);
            showSuccessToast('Message deleted successfully!');
            navigate(`/message/archived`);
        } catch (error: any) {
            showErrorToast(error?.response?.data?.message || 'Failed to delete message');
        } finally {
            setIsDeleting(false);
        }
    };

    const handleDeleteCancel = () => {
        setShowDeleteConfirm(false);
    };

    const handleRecallClick = () => {
        setShowRecallConfirm(true);
    };

    const handleRecallConfirm = async () => {
        setShowRecallConfirm(false);
        setIsRecalling(true);
        try {
            await recallMessage(message.id);
            showSuccessToast('Message recalled successfully!');
            if (message?.status === MessageStatus.Scheduled) {
                navigate('/message/draft');
            } else if (message?.status === MessageStatus.Published) {
                navigate('/message/archived');
            } else {
                navigate(-1);
            }
        } catch (error: any) {
            showErrorToast(error?.response?.data?.message || 'Failed to recall message');
        } finally {
            setIsRecalling(false);
        }
    };

    const handleRecallCancel = () => {
        setShowRecallConfirm(false);
    };

    // Render actions based on message status
    if (message.status === MessageStatus.Draft) {
        return (
            <>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View Logs">
                        <IconButton size="medium" onClick={handleViewLogs} disabled={isDeleting || isDuplicating}>
                            <HistoryOutlined style={{ color: DEFAULT_COLOR }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Edit">
                        <IconButton size="medium" onClick={handleEdit} disabled={isDeleting || isDuplicating}>
                            <EditOutlined style={{ color: PRIMARY_COLOR }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                        <IconButton size="medium" onClick={handleDuplicateClick} disabled={isDeleting || isDuplicating}>
                            <CopyOutlined style={{ color: SUCCESS_COLOR }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                        <IconButton size="medium" onClick={handleDeleteClick} disabled={isDeleting || isDuplicating}>
                            <DeleteOutlined style={{ color: ERROR_COLOR }} />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {/* Delete Confirmation Dialog */}
                <Dialog open={showDeleteConfirm} onClose={handleDeleteCancel}>
                    <DialogTitle>Are you sure?</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            <strong>This action cannot be undone.</strong> The message will be moved to the archived
                            section.
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDeleteCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDeleteConfirm} color="error" variant="contained" autoFocus>
                            Delete
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Duplicate Confirmation Dialog */}
                <Dialog open={showDuplicateConfirm} onClose={handleDuplicateCancel}>
                    <DialogTitle>Duplicate Message</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            A new draft message will be created based on this message's content. Do you want to
                            continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDuplicateCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDuplicateConfirm} color="primary" variant="contained" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    if (message.status === MessageStatus.Scheduled || message.status === MessageStatus.Published) {
        return (
            <>
                <Stack direction="row" spacing={0.5}>
                    <Tooltip title="View">
                        <IconButton size="medium" onClick={handleView} disabled={isDuplicating || isRecalling}>
                            <EyeOutlined style={{ color: PRIMARY_COLOR }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="View Logs">
                        <IconButton size="medium" onClick={handleViewLogs} disabled={isDeleting || isDuplicating}>
                            <HistoryOutlined style={{ color: DEFAULT_COLOR }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Duplicate">
                        <IconButton
                            size="medium"
                            onClick={handleDuplicateClick}
                            disabled={isDuplicating || isRecalling}
                        >
                            <CopyOutlined style={{ color: SUCCESS_COLOR }} />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Recall">
                        <IconButton size="medium" onClick={handleRecallClick} disabled={isDuplicating || isRecalling}>
                            <UndoOutlined style={{ color: ERROR_COLOR }} />
                        </IconButton>
                    </Tooltip>
                </Stack>

                {/* Recall Confirmation Dialog */}
                <Dialog open={showRecallConfirm} onClose={handleRecallCancel}>
                    <DialogTitle>Recall Message</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            {message.status === MessageStatus.Scheduled ? (
                                <>
                                    This message is scheduled for delivering, recall will move the message back to{' '}
                                    <strong>Draft</strong>.
                                </>
                            ) : (
                                <>
                                    This message is already published, recall will remove the notification history of
                                    recipients. <br />
                                    <strong>Note:</strong> Users may still interact with the notification banner from
                                    the system.
                                </>
                            )}
                            <br />
                            <br />
                            Do you want to continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleRecallCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleRecallConfirm} color="error" variant="contained" autoFocus>
                            Recall
                        </Button>
                    </DialogActions>
                </Dialog>

                {/* Duplicate Confirmation Dialog */}
                <Dialog open={showDuplicateConfirm} onClose={handleDuplicateCancel}>
                    <DialogTitle>Duplicate Message</DialogTitle>
                    <DialogContent>
                        <DialogContentText>
                            A new draft message will be created based on this message's content. Do you want to
                            continue?
                        </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleDuplicateCancel} color="inherit">
                            Cancel
                        </Button>
                        <Button onClick={handleDuplicateConfirm} color="primary" variant="contained" autoFocus>
                            OK
                        </Button>
                    </DialogActions>
                </Dialog>
            </>
        );
    }

    // For other statuses (Recalled, Removed), show limited actions
    return (
        <>
            <Stack direction="row" spacing={0.5}>
                <Tooltip title="View">
                    <IconButton size="medium" onClick={handleView} disabled={isDuplicating}>
                        <EyeOutlined style={{ color: PRIMARY_COLOR }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="View Logs">
                    <IconButton size="medium" onClick={handleViewLogs} disabled={isDeleting || isDuplicating}>
                        <HistoryOutlined style={{ color: DEFAULT_COLOR }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Duplicate">
                    <IconButton size="medium" onClick={handleDuplicateClick} disabled={isDuplicating}>
                        <CopyOutlined style={{ color: SUCCESS_COLOR }} />
                    </IconButton>
                </Tooltip>
            </Stack>

            {/* Duplicate Confirmation Dialog */}
            <Dialog open={showDuplicateConfirm} onClose={handleDuplicateCancel}>
                <DialogTitle>Duplicate Message</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        A new draft message will be created based on this message's content. Do you want to continue?
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDuplicateCancel} color="inherit">
                        Cancel
                    </Button>
                    <Button onClick={handleDuplicateConfirm} color="primary" variant="contained" autoFocus>
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default MessageActionsCell;
